<template>
  <div>
    <div class="containar">
      <div class="contents-block">
        <div class="preview-area">
          <div class="preview-header">
            <h3>プレビュー</h3>
          </div>
          <ul class="preview-list" :class="`preview-list--${variant}`">
            <li v-for="(color, index) in avatarColors" :key="color" class="preview-user">
              <div
                class="preview-avatar-frame"
                :class="`preview-avatar-frame--${variant}`"
                :style="frameStyle"
              >
                <img
                  :src="imgPath"
                  class="preview-avatar"
                  :style="{ backgroundColor: color }"
                  alt=""
                />
              </div>
              <span
                class="preview-name"
                :class="`preview-name--${variant}`"
                :style="{ backgroundColor: nameColor }"
              >User{{ index + 1 }}</span>
            </li>
          </ul>
        </div>

        <div class="set">
          <template v-if="hasPresets">
            <h3>カラープリセット</h3>
            <div class="preset-row">
              <button
                v-for="(preset, index) in presets"
                :key="index"
                type="button"
                class="preset-btn"
                :class="{ active: mainColor.hex === preset.main && subColor.hex === preset.sub }"
                :title="preset.name"
                @click="applyPreset(preset)"
              >
                <span class="preset-color preset-main" :style="{ background: preset.main }"></span>
                <span class="preset-color preset-sub" :style="{ background: preset.sub }"></span>
              </button>
            </div>

            <h3>カラー設定</h3>
            <div class="color-row">
              <label class="color-item">
                <span class="color-label">メイン</span>
                <div class="color-input-wrapper">
                  <input v-model="mainColor.hex" type="color" class="color-native" />
                  <span class="color-preview" :style="{ background: mainColor.hex }"></span>
                  <input v-model="mainColor.hex" type="text" class="color-hex-input" maxlength="7" />
                </div>
              </label>
              <label class="color-item">
                <span class="color-label">サブ</span>
                <div class="color-input-wrapper">
                  <input v-model="subColor.hex" type="color" class="color-native" />
                  <span class="color-preview" :style="{ background: subColor.hex }"></span>
                  <input v-model="subColor.hex" type="text" class="color-hex-input" maxlength="7" />
                </div>
              </label>
            </div>
          </template>

          <template v-else>
            <h3>名前のカラー選択</h3>
            <div class="color-picker-wrapper">
              <chrome-picker :value="colors" v-model="colors" />
            </div>
          </template>

          <h3>コピペ用CSS</h3>
          <div class="url-description">
            <p>DiscordオーバーレイのカスタムCSSです。</p>
            <p>
              <a href="https://reactive.fugi.tech/" target="_blank" rel="noopener noreferrer">Discord Reactive</a>
              でDiscordのボイスチャンネルのURLを取得→OBSでブラウザソースを追加し、URLを貼り付け→下記CSSをカスタムCSS欄にコピペ
            </p>
            <p>
              <a href="https://www.ruri.info/?p=188" target="_blank" rel="noopener noreferrer">さらに詳しい使い方はこちら</a>
            </p>
          </div>
          <div class="url-container">
            <textarea id="target" v-model="generatedCSS" readonly class="url-input textarea" />
            <button type="button" class="url-copy-btn" @click="copyCSS">
              <span>コピー</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chrome } from 'vue-color';

const PRESETS = [
  { name: 'ベビーピンク', main: '#F9A8D4', sub: '#FECDD3' },
  { name: 'さくらもち', main: '#FDA4AF', sub: '#FFE4E6' },
  { name: 'ストロベリー', main: '#FB7185', sub: '#FECDD3' },
  { name: 'そらいろ', main: '#7DD3FC', sub: '#BAE6FD' },
  { name: 'みずいろ', main: '#67E8F9', sub: '#CFFAFE' },
  { name: 'ラベンダー', main: '#C4B5FD', sub: '#E9D5FF' },
  { name: 'ミント', main: '#A8DF8E', sub: '#F0FFDF' },
  { name: 'れもん', main: '#FF9B00', sub: '#FFC900' },
  { name: 'ピーチ', main: '#FDBA74', sub: '#FED7AA' },
  { name: 'ミルクティー', main: '#D4A574', sub: '#E8D5C4' },
  { name: 'グレージュ', main: '#A8A29E', sub: '#D6D3D1' },
  { name: 'くもいろ', main: '#94A3B8', sub: '#CBD5E1' },
];

export default {
  name: 'DiscordTechDesign',
  components: {
    'chrome-picker': Chrome,
  },
  props: {
    variant: {
      type: String,
      required: true,
      validator: value => ['classic', 'peek', 'dream'].includes(value),
    },
  },
  data() {
    return {
      imgPath: require('@/assets/discord-icon.png'),
      colors: {
        hex: '#000000',
        hex8: '#000000FF',
        hsl: { h: 150, s: 0, l: 0, a: 1 },
        rgba: { r: 0, g: 0, b: 0, a: 1 },
        a: 1,
      },
      mainColor: { hex: '#F9A8D4' },
      subColor: { hex: '#FECDD3' },
      avatarColors: ['#5865F2', '#EB459E', '#57F287'],
      presets: PRESETS,
    };
  },
  computed: {
    hasPresets() {
      return this.variant !== 'classic';
    },
    nameColor() {
      return this.hasPresets ? this.mainColor.hex : this.colors.hex8;
    },
    frameStyle() {
      if (this.variant === 'classic') {
        return {
          outlineColor: this.colors.hex8,
          boxShadow: `4px 4px 0 0 ${this.colors.hex8}`,
        };
      }
      return {
        boxShadow: `0 0 0 8px ${this.subColor.hex}, 0 0 0 12px ${this.mainColor.hex}`,
      };
    },
    generatedCSS() {
      const avatarSize = this.variant === 'classic' ? 96 : 124;
      const gap = this.variant === 'classic' ? 18 : 32;
      const base = `#embed .h-screen{flex-direction:row!important;flex-wrap:wrap!important;justify-content:flex-start!important;align-items:flex-end!important;align-content:flex-end!important;padding:20px!important;}#embed .h-screen>.flex>.flex{gap:${gap}px!important;}#embed [data-discord-id]{flex:0 0 ${avatarSize}px!important;width:${avatarSize}px!important;height:${avatarSize}px!important;max-width:${avatarSize}px!important;box-sizing:content-box!important;align-items:flex-start!important;justify-content:flex-start!important;}#embed [data-discord-id]>.relative{width:${avatarSize}px!important;height:${avatarSize}px!important;aspect-ratio:1/1!important;position:relative!important;overflow:visible!important;}#embed [data-discord-id] canvas{display:block!important;width:${avatarSize}px!important;height:${avatarSize}px!important;max-width:none!important;max-height:none!important;aspect-ratio:1/1!important;box-sizing:border-box!important;object-fit:contain!important;position:relative!important;z-index:1!important;filter:brightness(90%);transition:filter 180ms ease-in-out;}#embed [data-discord-id][data-speaking="true"] canvas{filter:brightness(100%)!important;}#embed [data-discord-id] .text-center{display:block!important;color:#fff!important;-webkit-text-stroke:0!important;text-shadow:none!important;font-weight:600!important;box-sizing:border-box!important;text-align:center!important;text-overflow:ellipsis!important;overflow:hidden!important;white-space:nowrap!important;}`;

      if (this.variant === 'classic') {
        return `${base}#embed [data-discord-id]{padding-bottom:32px!important;}#embed [data-discord-id] canvas{border-radius:12px!important;outline:0.13rem solid ${this.colors.hex8}!important;box-shadow:4px 4px 0 0 ${this.colors.hex8}!important;}#embed [data-discord-id] .text-center{position:absolute!important;top:calc(100% + 8px)!important;right:auto!important;bottom:auto!important;left:50%!important;transform:translateX(-50%)!important;width:96px!important;font-size:12px!important;line-height:1.2!important;padding:4px 12px!important;border-radius:6px!important;background:${this.colors.hex8}!important;}`;
      }

      const font = `@import url('https://fonts.googleapis.com/css2?family=Zen+Maru+Gothic:wght@300&display=swap');`;
      const frame = `#embed [data-discord-id]>.relative::before{content:"";position:absolute;inset:0;width:124px;height:124px;border-radius:12px;box-shadow:0 0 0 8px ${this.subColor.hex},0 0 0 12px ${this.mainColor.hex};z-index:2;pointer-events:none;}#embed [data-discord-id] canvas{border-radius:12px!important;}#embed [data-discord-id] .text-center{font-family:"Zen Maru Gothic",sans-serif!important;font-size:16px!important;background:${this.mainColor.hex}!important;}`;

      if (this.variant === 'peek') {
        return `${font}${base}${frame}#embed [data-discord-id]{padding-bottom:24px!important;}#embed [data-discord-id] .text-center{position:absolute!important;z-index:0!important;top:calc(100% - 14px)!important;right:auto!important;bottom:auto!important;left:50%!important;transform:translateX(-50%)!important;width:96px!important;line-height:2!important;padding:16px 12px 0!important;border-radius:0 0 12px 12px!important;}`;
      }

      return `${font}${base}${frame}#embed [data-discord-id]{padding-bottom:12px!important;}#embed [data-discord-id] .text-center{position:absolute!important;z-index:3!important;top:calc(100% - 20px)!important;right:auto!important;bottom:auto!important;left:-12px!important;transform:none!important;width:96px!important;line-height:2!important;padding:0 16px!important;border-radius:0 24px 0 24px!important;}`;
    },
  },
  methods: {
    applyPreset(preset) {
      this.mainColor.hex = preset.main;
      this.subColor.hex = preset.sub;
    },
    copyCSS() {
      if (!navigator.clipboard) {
        alert('コピーに失敗しました。');
        return;
      }
      navigator.clipboard.writeText(this.generatedCSS).then(
        () => alert('CSSをコピーしました！'),
        () => alert('コピーに失敗しました。')
      );
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Zen+Maru+Gothic:wght@300&display=swap');

p { margin: 0; padding: 0; font-size: 14px; }
h3 { font-size: 14px; margin-top: 24px; margin-bottom: 0; }

.containar { margin: 0 auto; width: 100%; }

.contents-block {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-around;
  align-items: flex-start;
  margin: 24px auto;
  gap: 24px;
}

.set {
  flex: 1;
  max-width: 420px;
  padding: 8px 12px 24px 12px;
  background-color: #FFFEF9;
  border-radius: 10px;
}

.preview-area {
  flex: 1;
  max-width: 420px;
  min-width: 320px;
}

.preview-header {
  padding: 2px 12px;
  margin-bottom: 16px;
}

.preview-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  min-height: 200px;
  margin: 0;
  padding: 24px 16px;
  list-style: none;
  box-sizing: border-box;
}

.preview-list--classic {
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0;
  min-height: 120px;
  padding: 0;
}

.preview-list--dream { padding-bottom: 60px; }

.preview-user { position: relative; width: 96px; list-style: none; }
.preview-list--classic .preview-user { width: 111px; padding-left: 15px; box-sizing: border-box; }

.preview-avatar-frame,
.preview-avatar {
  width: 96px;
  height: 96px;
  border-radius: 12px;
  box-sizing: border-box;
}

.preview-avatar-frame { position: relative; z-index: 0; }
.preview-avatar { position: relative; z-index: 1; display: block; object-fit: scale-down; padding: 5px; }
.preview-avatar-frame--classic { outline: 2px solid; }
.preview-avatar-frame--peek { z-index: 2; }

.preview-name {
  display: block;
  color: #fff;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
}

.preview-name--classic {
  position: relative;
  width: 88px;
  margin: 8px 4px 0;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
}

.preview-name--peek {
  position: relative;
  z-index: 0;
  width: fit-content;
  max-width: 96px;
  margin: -16px auto 0;
  padding: 22px 12px 0;
  border-radius: 0 0 12px 12px;
  font: 14px/2 "Zen Maru Gothic", sans-serif;
}

.preview-name--dream {
  position: absolute;
  z-index: 2;
  bottom: -8px;
  left: -12px;
  width: fit-content;
  max-width: 96px;
  margin: 0 auto;
  padding: 0 18px;
  border-radius: 0 24px 0 24px;
  font: 13px/2 "Zen Maru Gothic", sans-serif;
}

.preset-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.preset-btn {
  display: flex;
  gap: 2px;
  padding: 4px;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.preset-btn:hover {
  border-color: #94a3b8;
  transform: translateY(-1px);
}

.preset-btn.active {
  border-color: #0f172a;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.1);
}

.preset-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.preset-main { border-radius: 4px 0 0 4px; }
.preset-sub { border-radius: 0 4px 4px 0; }

.color-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.color-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.color-label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.color-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: border-color 0.2s;
  position: relative;
}

.color-input-wrapper:hover { border-color: #cbd5e1; }
.color-input-wrapper:focus-within { border-color: #0f172a; }

.color-native {
  position: absolute;
  width: 32px;
  height: 32px;
  opacity: 0;
  cursor: pointer;
}

.color-preview {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) inset;
  cursor: pointer;
  flex-shrink: 0;
}

.color-hex-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
  color: #334155;
  text-transform: uppercase;
  padding: 0;
}

.color-hex-input:focus { outline: none; }

.color-picker-wrapper { margin-top: 12px; }

.color-picker-wrapper .vc-chrome {
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 0 0 1px #e2e8f0;
  font-family: system-ui, -apple-system, sans-serif;
}

.url-description { margin: 12px 0 16px; }
.url-description p { font-size: 13px; color: #718096; margin: 0 0 6px; line-height: 1.5; }
.url-description a { color: #0f172a; text-decoration: underline; }
.url-description a:hover { color: #1e293b; }

.url-container {
  display: flex;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 16px;
}

.url-input {
  flex: 1;
  height: 2.4em;
  padding: 0 12px;
  border-radius: 4px;
  border: none;
  box-shadow: 0 0 0 1px #ccc inset;
  font-size: 12px;
  background: #f8fafc;
  color: #4a5568;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.4;
  resize: vertical;
}

.url-input.textarea {
  height: 80px;
  padding: 12px;
}

.url-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #0f172a;
}

.url-copy-btn {
  padding: 0 20px;
  height: 2.4em;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.url-copy-btn:hover {
  background: #0f172a;
  transform: translateY(-1px);
}

@media screen and (max-width: 767px) {
  .contents-block { display: block; margin: 0 auto; }
  .set { margin-top: 24px; margin-left: 0; }
}
</style>
